import { getValueFilters } from "PFApp/use_filtered_collection";
import { checkFiltersAreClear } from "PFApp/use_filtered_collection/format_filters";
import { useFilters } from "PFApp/use_filtered_collection/use_filters";
import { Filter, Filters, Meta, OrderOption, Value } from "PFTypes";
import { useCallback, useMemo } from "react";

import { useMarketplaceContext } from "../contexts/marketplace_context";
import { handleTermOrderClear } from "../helpers/handle_term_order_clear";
import { useWorkOpportunitiesTab } from "../work_opportunities/hooks/use_work_opportunities_tab";
import { useStartDateFilterUrlSync } from "./use_start_date_filter_url_sync";

type UseMarketplaceFiltersReturn = {
  clearAllFilters: VoidFunction;
  handleFilterChange: (filter: Filter, value: Value) => void;
  handleFiltersReplace: (filters: Filters<Value>) => void;
  handleOrderChange: (order: OrderOption) => void;
  areFiltersCleared: boolean;
};

export const useMarketplaceFilters = (meta?: Meta): UseMarketplaceFiltersReturn => {
  const currentTab = useWorkOpportunitiesTab();
  const { setOrderData, setFiltersData } = useMarketplaceContext();
  const handleStartDateFilterSync = useStartDateFilterUrlSync();

  const setSelectedFilters = useCallback(
    (value) => setFiltersData(value, currentTab!),
    [setFiltersData, currentTab]
  );

  const selectedFilters = useMemo(() => getValueFilters(meta?.filters), [meta]);

  const handleOrderChange = useCallback(
    (order) => setOrderData(order, currentTab!),
    [setOrderData, currentTab]
  );

  const clearOrder = useCallback(() => handleOrderChange(null), [handleOrderChange]);

  const { setFilters, updateFilter, resetFilters } = useFilters({
    selectedFilters,
    setSelectedFilters
  });

  const areFiltersCleared = useMemo(() => checkFiltersAreClear(meta?.filters, []), [meta]);

  const clearAllFilters = useCallback(() => {
    handleTermOrderClear(selectedFilters, {}, clearOrder, currentTab);
    resetFilters({});
    handleStartDateFilterSync(null, currentTab);
  }, [clearOrder, currentTab, selectedFilters, resetFilters, handleStartDateFilterSync]);

  const handleFilterChange = useCallback(
    (filter, value) => {
      handleTermOrderClear(selectedFilters, value, clearOrder, currentTab);
      filter.name === "start_date" && handleStartDateFilterSync(value, currentTab);
      updateFilter(filter, value);
    },
    [handleStartDateFilterSync, clearOrder, currentTab, updateFilter, selectedFilters]
  );

  const handleFiltersReplace = useCallback(
    (filters) => {
      handleTermOrderClear(selectedFilters, filters, clearOrder, currentTab);
      handleStartDateFilterSync(filters, currentTab);
      setFilters(filters);
    },
    [handleStartDateFilterSync, clearOrder, currentTab, setFilters, selectedFilters]
  );

  return { handleFilterChange, clearAllFilters, handleFiltersReplace, handleOrderChange, areFiltersCleared };
};
